import { useAuthStore } from '@/stores/authStore'
import { authBreadcrumb, setSentryUser } from '@/api/authDiagnostics'
import { AUTH_PERSIST_KEY } from '@/utils/constants'

/**
 * Синхронизация сессии между вкладками одного браузера.
 *
 * Сессия лежит в общем localStorage, но zustand держит копию в памяти каждой
 * вкладки и сам чужие записи не подхватывает. Без синхронизации вкладка, чей
 * токен уже обменяла соседняя, шла обменивать устаревший токен из памяти и
 * получала отказ — это и выкидывало на вход. Событие `storage` приходит во все
 * вкладки, кроме той, что писала: по нему память перечитывается из хранилища.
 */

let started = false

interface SessionIdentity {
  isAuthenticated: boolean
  userId: number | null
  isImpersonating: boolean
}

function identity(): SessionIdentity {
  const state = useAuthStore.getState()

  return {
    isAuthenticated: state.isAuthenticated,
    userId: state.user?.id ?? null,
    isImpersonating: state.isImpersonating,
  }
}

function onStorage(event: StorageEvent): void {
  // key === null — localStorage.clear() в другой вкладке
  if (event.key !== AUTH_PERSIST_KEY && event.key !== null) {
    return
  }
  if (event.storageArea !== null && event.storageArea !== window.localStorage) {
    return
  }

  const before = identity()
  void useAuthStore.persist.rehydrate()
  const after = identity()

  if (before.isAuthenticated && !after.isAuthenticated) {
    // Выход в другой вкладке: убрать следы сессии здесь (сокет, справочники,
    // онбординг). Маршруты сами уведут на вход по isAuthenticated.
    authBreadcrumb('session_ended_in_other_tab', 'info')
    useAuthStore.getState().logout()
    return
  }

  const userChanged = before.userId !== null && after.userId !== null && before.userId !== after.userId
  const switched =
    before.isAuthenticated &&
    after.isAuthenticated &&
    (userChanged || before.isImpersonating !== after.isImpersonating)
  if (switched) {
    // В другой вкладке вошли другим пользователем или под пользователем:
    // данные страницы принадлежат прежнему — начать с чистого листа.
    authBreadcrumb('session_switched_in_other_tab', 'info')
    window.location.reload()
  }
}

/** Подключить один раз при старте приложения (main.tsx) */
export function initAuthSessionSync(): void {
  if (started || typeof window === 'undefined') {
    return
  }
  started = true

  window.addEventListener('storage', onStorage)

  setSentryUser(useAuthStore.getState().user?.id ?? null)
  useAuthStore.subscribe((state, previous) => {
    const userId = state.user?.id ?? null
    if (userId !== (previous.user?.id ?? null)) {
      setSentryUser(userId)
    }
  })
}
